import React from 'react';
import { Check, ArrowRight, Users, Clock, Briefcase, TrendingUp, Award, BookOpen } from 'lucide-react';
import { PageTransition } from '../../components/layout/PageTransition';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { DecorativeShape } from '../../components/DecorativeShape';
import { Animate } from '../../components/ui/Animate';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
export function FirstTimeManager() {
  const {
    t
  } = useLanguage();
  const benefits = t('firstTimeManager.benefits') as string[];
  const curriculum = t('firstTimeManager.curriculum') as Array<{
    week: string;
    title: string;
    desc: string;
  }>;
  const forWhomItems = t('firstTimeManager.forWhomItems') as string[];
  const forWhomIcons = [<TrendingUp className="text-teal shrink-0" size={20} data-id="element-556" />, <Briefcase className="text-teal shrink-0" size={20} data-id="element-557" />, <BookOpen className="text-teal shrink-0" size={20} data-id="element-558" />];
  return <PageTransition data-id="element-559">
      <div className="pt-12 pb-24" data-id="element-560">
        <section className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-20" data-id="element-561">
          <DecorativeShape type="square" color="bg-teal/10" className="top-0 left-0 w-[500px] h-[500px] -translate-x-1/4 -translate-y-1/4 -z-10 rotate-12" data-id="element-562" />
          <DecorativeShape type="donut" color="border-coral/10" className="bottom-0 right-0 w-64 h-64 translate-x-1/4 translate-y-1/4 -z-10" data-id="element-563" />
          <Animate className="text-center max-w-4xl mx-auto" data-id="element-564">
            <span className="inline-block px-4 py-2 bg-teal/10 text-teal font-medium rounded-full text-sm mb-6" data-id="element-565">
              {t('firstTimeManager.badge')}
            </span>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight" data-id="element-566">
              {t('firstTimeManager.title')} <br data-id="element-567" />
              <span className="text-teal" data-id="element-568">
                {t('firstTimeManager.titleHighlight')}
              </span>
            </h1>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed" data-id="element-569">
              {t('firstTimeManager.subtitle')}
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm font-medium text-gray-500 mb-10" data-id="element-570">
              <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm border border-gray-100" data-id="element-571">
                <Clock size={18} className="text-teal" data-id="element-572" />
                <span data-id="element-573">{t('firstTimeManager.duration')}</span>
              </div>
              <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm border border-gray-100" data-id="element-574">
                <Users size={18} className="text-teal" data-id="element-575" />
                <span data-id="element-576">{t('firstTimeManager.audience')}</span>
              </div>
              <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm border border-gray-100" data-id="element-577">
                <Award size={18} className="text-teal" data-id="element-578" />
                <span data-id="element-579">{t('firstTimeManager.certificate')}</span>
              </div>
            </div>
            <Link to="/contact" data-id="element-580">
              <Button variant="secondary" size="lg" rightIcon={<ArrowRight size={20} data-id="element-582" />} data-id="element-581">
                {t('firstTimeManager.bookCta')}
              </Button>
            </Link>
          </Animate>
        </section>

        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-24" data-id="element-583">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12" data-id="element-584">
            <div className="lg:col-span-2 space-y-12" data-id="element-585">
              <Animate data-id="element-586">
                <h2 className="text-3xl font-bold mb-6 text-charcoal" data-id="element-587">
                  {t('firstTimeManager.aboutTitle')}
                </h2>
                <div className="prose prose-lg text-gray-600" data-id="element-588">
                  <p className="mb-4" data-id="element-589">{t('firstTimeManager.aboutP1')}</p>
                  <p data-id="element-590">{t('firstTimeManager.aboutP2')}</p>
                </div>
              </Animate>
              <Animate delay={0.1} data-id="element-591">
                <h3 className="text-2xl font-bold mb-6 text-charcoal" data-id="element-592">
                  {t('firstTimeManager.learnTitle')}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-593">
                  {benefits.map((benefit: string, index: number) => <div key={index} className="flex items-start gap-3" data-id="element-594">
                      <div className="mt-1 p-1 rounded-full bg-teal/10 text-teal flex-shrink-0" data-id="element-595">
                        <Check size={14} strokeWidth={3} data-id="element-596" />
                      </div>
                      <span className="text-gray-700" data-id="element-597">{benefit}</span>
                    </div>)}
                </div>
              </Animate>
              <Animate delay={0.15} data-id="element-598">
                <h3 className="text-2xl font-bold mb-6 text-charcoal" data-id="element-599">
                  {t('firstTimeManager.scheduleTitle')}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-600">
                  {curriculum.map((item, index) => <Card key={index} className="p-6 border-t-4 border-teal bg-white h-full" data-id="element-601">
                      <span className="text-xs font-bold text-teal uppercase tracking-wide mb-2 block" data-id="element-602">
                        {item.week}
                      </span>
                      <h4 className="text-lg font-bold text-charcoal mb-2" data-id="element-603">
                        {item.title}
                      </h4>
                      <p className="text-sm text-gray-600" data-id="element-604">{item.desc}</p>
                    </Card>)}
                </div>
              </Animate>
            </div>
            <div className="lg:col-span-1" data-id="element-605">
              <div className="sticky top-24 space-y-8" data-id="element-606">
                <Card className="p-8 bg-white shadow-xl border-t-4 border-teal" data-id="element-607">
                  <h3 className="text-xl font-bold mb-6 text-charcoal" data-id="element-608">
                    {t('firstTimeManager.forWhom')}
                  </h3>
                  <ul className="space-y-4 mb-8" data-id="element-609">
                    {forWhomItems.map((item: string, i: number) => <li key={i} className="flex items-start gap-3" data-id="element-610">
                        {forWhomIcons[i]}
                        <span className="text-gray-600" data-id="element-611">{item}</span>
                      </li>)}
                  </ul>
                  <Link to="/contact" className="block" data-id="element-612">
                    <Button variant="secondary" className="w-full" rightIcon={<ArrowRight size={18} data-id="element-614" />} data-id="element-613">
                      {t('firstTimeManager.inquiry')}
                    </Button>
                  </Link>
                </Card>
                <div className="bg-teal/5 rounded-3xl p-8 text-center" data-id="element-615">
                  <div className="w-16 h-16 mx-auto bg-white rounded-full flex items-center justify-center shadow-md mb-4 text-teal" data-id="element-616">
                    <Users size={32} data-id="element-617" />
                  </div>
                  <h4 className="font-bold text-lg mb-2" data-id="element-618">
                    {t('firstTimeManager.groupTitle')}
                  </h4>
                  <p className="text-gray-600 text-sm mb-4" data-id="element-619">
                    {t('firstTimeManager.groupDesc')}
                  </p>
                  <Link to="/contact" className="text-teal font-bold hover:underline" data-id="element-620">
                    {t('firstTimeManager.contactLink')}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="max-w-4xl mx-auto px-4 text-center" data-id="element-621">
          <Animate data-id="element-622">
            <Card className="p-12 bg-gradient-to-br from-teal/5 to-green-50 border-0" data-id="element-623">
              <h2 className="text-3xl font-bold mb-4" data-id="element-624">
                {t('firstTimeManager.ctaTitle')}
              </h2>
              <p className="text-gray-600 mb-8 max-w-xl mx-auto" data-id="element-625">
                {t('firstTimeManager.ctaDesc')}
              </p>
              <Link to="/contact" data-id="element-626">
                <Button variant="secondary" rightIcon={<ArrowRight size={18} data-id="element-628" />} data-id="element-627">
                  {t('firstTimeManager.ctaCta')}
                </Button>
              </Link>
            </Card>
          </Animate>
        </section>
      </div>
    </PageTransition>;
}